import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Space, 
  Tag, 
  Dropdown, 
  Modal,
  message,
  Tooltip,
  Avatar,
  Typography,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PlayCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  RobotOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  NodeIndexOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiUtils } from '../../lib/api';

const { Title, Text } = Typography;

interface Agent {
  id: string;
  name: string;
  description?: string;
  version: string;
  nodes: any[];
  edges: any[];
  metadata?: any;
  isActive: boolean;
  isDefault: boolean;
  lastExecuted?: string;
  executionCount: number;
  createdAt: string;
  updatedAt: string;
  projectId: string;
  project?: {
    id: string;
    name: string;
    dataSources?: any[];
  };
}

interface DataSource {
  id: string;
  name: string;
  description?: string;
  connectorType: string;
  status: string;
}

interface AgentListViewProps {
  projectId: string;
  onEditAgent: (agentId: string) => void;
  onCreateAgent: () => void;
}

const AgentListView: React.FC<AgentListViewProps> = ({
  projectId,
  onEditAgent,
  onCreateAgent,
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgents();
    loadDataSources();
  }, [projectId]);

  const loadAgents = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const response = await apiUtils.backend.get(`/projects/${projectId}/graphs`);
      setAgents(response.data || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
      message.error('Failed to load AI agents');
    } finally {
      setLoading(false);
    }
  };

  const loadDataSources = async () => {
    if (!projectId) return;
    
    try {
      // Load project with data sources
      const dataSourcesResponse = await apiUtils.backend.get(`/data-sources?projectId=${projectId}`);
      setDataSources(dataSourcesResponse.data || []);
    } catch (error) {
      console.error('Failed to load data sources:', error);
    }
  };


  const handleDeleteAgent = async (agentId: string) => {
    Modal.confirm({
      title: 'Delete AI Agent',
      content: 'Are you sure you want to delete this AI agent? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await apiUtils.backend.delete(`/projects/${projectId}/graphs/${agentId}`);
          message.success('AI Agent deleted successfully');
          loadAgents();
        } catch (error) {
          console.error('Failed to delete agent:', error);
          message.error('Failed to delete AI agent');
        }
      },
    });
  };

  const handleExecuteAgent = async (agent: Agent) => {
    const userInput = prompt('Enter input for AI agent execution:', 'Test input');
    if (!userInput) return;

    try {
      const response = await apiUtils.backend.post(`/projects/${projectId}/graphs/${agent.id}/execute`, {
        input: userInput,
        initialState: {}
      });
      
      Modal.info({
        title: 'Execution Result',
        content: (
          <div>
            <p><strong>Input:</strong> {userInput}</p>
            <p><strong>Output:</strong> {response.data.finalState?.output || 'No output'}</p>
            <p><strong>Status:</strong> {response.data.success ? 'Success' : 'Failed'}</p>
            {response.data.timings?.total && (
              <p><strong>Time:</strong> {response.data.timings.total}ms</p>
            )}
          </div>
        ),
        width: 500,
      });
      
      loadAgents(); // Refresh to update execution count
    } catch (error) {
      console.error('Failed to execute agent:', error);
      message.error('Failed to execute AI agent');
    }
  };

  const getStatusColor = (agent: Agent) => {
    if (!agent.isActive) return 'default';
    if (agent.isDefault) return 'gold';
    if (agent.nodes?.length === 0) return 'orange';
    return 'green';
  };

  const getStatusText = (agent: Agent) => {
    if (!agent.isActive) return 'Inactive';
    if (agent.isDefault) return 'Default';
    if (agent.nodes?.length === 0) return 'Empty';
    return 'Active';
  };

  const columns: ColumnsType<Agent> = [
    {
      title: 'Agent',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Agent) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar 
            icon={<RobotOutlined />} 
            style={{ backgroundColor: '#1890ff' }}
          />
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{name}</div>
            {record.description && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Structure',
      key: 'structure',
      render: (_, record: Agent) => (
        <Space direction="vertical" size={4}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NodeIndexOutlined style={{ color: '#666' }} />
            <Text style={{ fontSize: 12 }}>
              {record.nodes?.length || 0} nodes, {record.edges?.length || 0} edges
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            v{record.version}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Data Sources',
      key: 'dataSources',
      render: (_, record: Agent) => {
        const linkedDataSources = record.metadata?.dataSourceIds || [];
        if (linkedDataSources.length === 0) {
          return <Text type="secondary" style={{ fontSize: 12 }}>No data sources</Text>;
        }

        return (
          <Space wrap>
            {linkedDataSources.map((dsId: string) => {
              const ds = (dataSources || []).find(d => d.id === dsId);
              return ds ? (
                <Tag 
                  key={dsId} 
                  icon={<DatabaseOutlined />}
                  color={ds.status === 'active' ? 'green' : 'orange'}
                  style={{ fontSize: 11 }}
                >
                  {ds.name}
                </Tag>
              ) : (
                <Tag key={dsId} color="red" style={{ fontSize: 11 }}>
                  Unknown
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record: Agent) => (
        <Tag color={getStatusColor(record)}>
          {getStatusText(record)}
        </Tag>
      ),
    },
    {
      title: 'Activity',
      key: 'activity',
      render: (_, record: Agent) => (
        <Space direction="vertical" size={4}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <PlayCircleOutlined style={{ color: '#666', fontSize: 12 }} />
            <Text style={{ fontSize: 12 }}>
              {record.executionCount} executions
            </Text>
          </div>
          {record.lastExecuted && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ClockCircleOutlined style={{ color: '#666', fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {new Date(record.lastExecuted).toLocaleDateString()}
              </Text>
            </div>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: Agent) => (
        <Space>
          <Tooltip title="Edit Agent">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEditAgent(record.id)}
              size="small"
            />
          </Tooltip>
          
          <Tooltip title="Execute Agent">
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              onClick={() => handleExecuteAgent(record)}
              size="small"
              disabled={!record.isActive || (record.nodes?.length || 0) === 0}
            />
          </Tooltip>

          <Dropdown
            menu={{
              items: [
                {
                  key: 'view',
                  icon: <EyeOutlined />,
                  label: 'View Details',
                  onClick: () => {
                    Modal.info({
                      title: record.name,
                      content: (
                        <div>
                          <p><strong>Description:</strong> {record.description || 'No description'}</p>
                          <p><strong>Nodes:</strong> {record.nodes?.length || 0}</p>
                          <p><strong>Edges:</strong> {record.edges?.length || 0}</p>
                          <p><strong>Version:</strong> {record.version}</p>
                          <p><strong>Created:</strong> {new Date(record.createdAt).toLocaleString()}</p>
                          <p><strong>Updated:</strong> {new Date(record.updatedAt).toLocaleString()}</p>
                        </div>
                      ),
                    });
                  },
                },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Delete',
                  danger: true,
                  onClick: () => handleDeleteAgent(record.id),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const statsCards = [
    {
      title: 'Total Agents',
      value: agents.length,
      icon: <RobotOutlined style={{ color: '#1890ff' }} />,
    },
    {
      title: 'Active Agents',
      value: agents.filter(a => a.isActive && a.nodes?.length > 0).length,
      icon: <PlayCircleOutlined style={{ color: '#52c41a' }} />,
    },
    {
      title: 'Data Sources',
      value: (dataSources || []).length,
      icon: <DatabaseOutlined style={{ color: '#722ed1' }} />,
    },
    {
      title: 'Total Executions',
      value: agents.reduce((sum, agent) => sum + agent.executionCount, 0),
      icon: <ClockCircleOutlined style={{ color: '#fa8c16' }} />,
    },
  ];

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto', background: '#ffffff' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Your AI Agents
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateAgent}
            size="large"
          >
            Create New Agent
          </Button>
        </div>
        
        {/* Stats Cards */}
        <Row gutter={16}>
          {statsCards.map((stat, index) => (
            <Col span={6} key={index}>
              <Card size="small">
                <Statistic
                  title={stat.title}
                  value={stat.value}
                  prefix={stat.icon}
                  valueStyle={{ fontSize: 24 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Agents Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={agents}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} agents`,
          }}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <RobotOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>No AI agents found</div>
                <div style={{ fontSize: 14, color: '#999', marginBottom: 16 }}>
                  Create your first AI agent to get started
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={onCreateAgent} size="large">
                  Create New Agent
                </Button>
              </div>
            ),
          }}
        />
      </Card>

    </div>
  );
};

export default AgentListView;